import { useEffect } from "react";
import { create } from "zustand";
import type { BreadcrumbCrumb } from "./globalBreadcrumb.logic";

interface ParentState {
  readonly pathname: string | null;
  readonly parent: BreadcrumbCrumb | null;
  readonly publish: (pathname: string, parent: BreadcrumbCrumb | null) => void;
  readonly clear: (pathname: string) => void;
}

const useParentStore = create<ParentState>((set) => ({
  pathname: null,
  parent: null,
  publish: (pathname, parent) => set({ pathname, parent }),
  clear: (pathname) =>
    set((state) => (state.pathname === pathname ? { pathname: null, parent: null } : state)),
}));

export function usePublishBreadcrumbParent(pathname: string, parent: BreadcrumbCrumb | null): void {
  const label = parent?.label;
  const key = parent?.key;
  const to = parent?.to;
  const name = parent?.icon?.name;
  const environmentId = parent?.icon?.environmentId;
  const cwd = parent?.icon?.cwd;
  const icon = parent?.icon?.canonicalIdentity?.icon;
  const iconUrl = parent?.icon?.canonicalIdentity?.iconUrl;
  const hasCanonicalIdentity = parent?.icon?.canonicalIdentity !== undefined;
  useEffect(() => {
    useParentStore.getState().publish(
      pathname,
      key && label
        ? {
            key,
            label,
            ...(to ? { to } : {}),
            ...(name
              ? {
                  icon: {
                    name,
                    environmentId,
                    cwd,
                    canonicalIdentity: hasCanonicalIdentity
                      ? { icon: icon ?? null, iconUrl: iconUrl ?? null }
                      : undefined,
                  },
                }
              : {}),
          }
        : null,
    );
    return () => useParentStore.getState().clear(pathname);
  }, [pathname, key, label, to, name, environmentId, cwd, icon, iconUrl, hasCanonicalIdentity]);
}

export function usePublishedBreadcrumbParent(pathname: string): BreadcrumbCrumb | null {
  return useParentStore((state) =>
    state.pathname === pathname.replace(/\/$/, "") ? state.parent : null,
  );
}
